<template>
  <div class="two-fa mt-0">
    <v-btn
      class="mt-4 elevation-0"
      fab
      dark
      small
      color="#1e1e1e"
      style="position: absolute; z-index: 10"
      @click="backToLoginCredentials()">
      <v-icon dark>mdi-arrow-left</v-icon>
    </v-btn>

    <div>
      <div
        class="fill-height d-flex flex-column align-center justify-center"
        v-if="askToRegister2fa">
        <figure>
          <img width="200" :src="'data:image/png;base64,' + qrCode" />
        </figure>
        <p class="mb-0 text-center">
          Escaneie o QRCode no seu app de autenticação e ative com o token apresentado
        </p>
      </div>

      <div v-else>
        <v-icon dark size="128" color="#1e1e1e" class="d-flex justify-center my-8"
          >mdi-shield-key</v-icon
        >
      </div>

      <v-text-field
        autofocus
        height="50"
        color="#1e1e1e text-heading-6"
        autocomplete="off"
        type="number"
        placeholder="000000"
        class="input-center default-width margin-auto"
        v-model="twoFaToken"
        :rules="[(v) => !!v || 'Insira o código de vericação']"></v-text-field>
      <div class="text-center">
        <v-btn
          depressed
          width="200"
          large
          dark
          color="#1e1e1e"
          :loading="loading"
          @click="validate(twoFaToken)"
          >Entrar</v-btn
        >
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import { validateTwoFaToken } from "@/services/auth";
import { removeAuthToken, saveAuthToken } from "@/utils/token";
import { twoFaByUser, activate2FaByUser } from "@/services/user";
import { Mutation } from "vuex-class";

@Component
export default class TwoFactorAuth extends Vue {
  twoFaToken = "";
  loading = false;
  document!: string | number;
  $refs!: HTMLFormElement;
  qrCode = "";
  totp_key = "";

  created(): void {
    if (localStorage.getItem("2fa") === "register_2fa") {
      twoFaByUser()
        .then((res: any) => {
          this.qrCode = res.data.qr_code;
          this.totp_key = res.data.totp_key;
        })
        .catch(() => {
          this.$emit("snackbar", {
            message: "Não foi possível obter o QR code!",
          });
        });
    }
  }

  @Watch("twoFaToken")
  async twoFaTokenWatch(val: number): Promise<void> {
    if (val.toString().length === 6) this.validate(this.twoFaToken);
  }

  @Mutation("setAuthToken", { namespace: "loginState" })
  setAuthToken!: (token: string | null) => void;

  get askToRegister2fa(): boolean {
    return localStorage.getItem("2fa") === "register_2fa";
  }

  get isMobile(): boolean {
    if (this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm") {
      return true;
    }
    return false;
  }

  backToLoginCredentials(): void {
    removeAuthToken();
    this.setAuthToken(null);
  }

  sendToChangePassword(token: string): void {
    this.$router.push({
      name: "changepassword",
      query: { token: token },
    });
  }

  handleValidateError(e: any): void {
    const errorCode = e.response?.data.errors[0].code;
    const errorStatus = e.response?.status;
    const tokenChangePassword = e.response?.data?.errors[0]?.token_change_password;

    if (!errorCode && !errorStatus) {
      this.$emit("snackbar", {
        message: "Ocorreu algum erro, tente novamente mais tarde",
      });
    }

    if (errorCode) {
      if (errorCode === 4018) {
        this.$emit("snackbar", {
          top: true,
          multiLine: true,
          color: "red",
          textStyle: "font-weight: 600;",
          transition: "scroll-y-transition",
          message:
            "Usuário desativado após muitas tentativas de login. Entre em contato com o suporte para reativar sua conta.",
        });

        this.backToLoginCredentials();
      }
      if (errorCode === 4019) {
        this.$emit("snackbar", {
          top: true,
          multiLine: true,
          color: "warning",
          timeout: 10000,
          textStyle: "font-weight: 600;",
          transition: "scroll-y-transition",
          message: e.response?.data.errors[0].description,
        });
      }
      if (errorCode === 4008) {
        this.$emit("snackbar", {
          top: true,
          multiLine: true,
          color: "red",
          textStyle: "font-weight: 600;",
          transition: "scroll-y-transition",
          message:
            "Este usuário está desativado. Entre em contato com o suporte para reativar sua conta.",
        });
      }
      if (errorCode === 4003) {
        this.$emit("snackbar", {
          top: true,
          multiLine: true,
          color: "red",
          textStyle: "font-weight: 600;",
          transition: "scroll-y-transition",
          message: "Código inválido",
        });
      }
      if (errorCode === 4016) {
        this.$router.push({
          name: "changepassword",
          query: { token: tokenChangePassword },
        });
      }
    }

    if (errorStatus) {
      if (errorStatus === 429) {
        this.$emit("snackbar", {
          message: "Muitas tentativas, aguarde alguns instantes.",
        });
      }
      if (errorStatus === 500) {
        this.$emit("snackbar", {
          message: "Erro ao tentar se conectar com o servidor.",
        });
      }
    }
  }

  async validate(twoFaToken: string): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    try {
      if (localStorage.getItem("2fa") === "validate_2fa") {
        const req: any = await validateTwoFaToken({ code: twoFaToken });
        saveAuthToken(req.data.token);

        if (this.$route.query.continue) {
          const query: any = this.$route.query;
          this.$router.push({ path: decodeURIComponent(query.continue) });
        } else if (req.data.ask_for && req.data.ask_for === "change_password") {
          this.sendToChangePassword(req.data.token);
        } else {
          this.$router.push({ name: "my-apps" });
        }
      } else {
        const res: any = await activate2FaByUser({
          code: twoFaToken,
          _2fa: "TOTP",
          totp_key: this.totp_key,
        });
        saveAuthToken(res.data.token);
        this.$router.push({ name: "my-apps" });
      }
    } catch (e: any) {
      this.handleValidateError(e);
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style lang="scss" scoped>
@import "@/assets/styles/utils/_variables.scss", "@/assets/styles/utils/_mixins.scss";

.input-center {
  position: relative;
  top: -10px;
}

.default-width {
  max-width: 200px;
}

.margin-auto {
  margin: auto !important;
}

.two-fa {
  padding: 0 20px;
  min-height: 400px;
  figure {
    text-align: center;
  }
  &-image {
    width: 100px;
  }
}
</style>
